import { Button, Input, Spin, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../constants';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import UsersTable from '../Elements/Tables/UsersTable';
import { selectAllClients } from '../../modules/client/stores/slice';
import {
  doBroadcastClientsNotification,
  doGetClients,
  doUpdateClientStatus,
  doDeleteClientUser,
} from '../../modules/client/stores/thunk';

const ClientsPage = () => {
  const dispatch = useDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null); // { record, action }
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  let location = useLocation();
  let navigate = useNavigate();
  const isFetching = useSelector((state) => state.client.isFetching);
  const metadata = useSelector((state) => state.client.metadata);
  const clients = useSelector((state) => selectAllClients(state));

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  const query = Object.fromEntries(searchParams);
  const currentPage = query.current || 1;
  const itemsPerPage = query.pageSize || PAGE_SIZE;
  const pagination = {
    current: currentPage || 1,
    pageSize: itemsPerPage,
    locale: { items_per_page: 'Clients/Page' },
    total: metadata.totalItems || clients.length,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showSizeChanger: true,
    showTotal: (total) => `${total} Clients`,
  };

  useEffect(() => {
    dispatch(doGetClients({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.showSizeChanger;
    delete params.total;
    delete params.locale;
    delete params.page;
    if (updatePath) {
      navigate({
        pathname: location.pathname,
        search: queryString.stringify(params || {}, {
          skipNull: true,
        }),
      });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetClients({
        page: newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const handleChangeBroadcastMessage = (e) => {
    setBroadcastMessage(e.target.value);
  };

  const handleSendBroadcast = async () => {
    const trimmed = broadcastMessage.trim();
    if (!trimmed) {
      return;
    }
    setIsConfirmModalOpen(true);
  };

  const handleConfirmBroadcast = async () => {
    const trimmed = broadcastMessage.trim();
    try {
      await dispatch(
        doBroadcastClientsNotification({
          body: trimmed,
          clientIds: selectedRowKeys && selectedRowKeys.length ? selectedRowKeys : undefined,
        }),
      ).unwrap();
      setBroadcastMessage('');
      setIsConfirmModalOpen(false);
    } catch (e) {
      // error is handled globally via redux slice
    }
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleStatusChange = (record, action) => {
    setStatusAction({ record, action });
    setIsStatusModalOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!statusAction) return;
    const { record, action } = statusAction;
    try {
      if (action === 'delete') {
        await dispatch(doDeleteClientUser({ id: record.id })).unwrap();
      } else {
        await dispatch(doUpdateClientStatus({ id: record.id, isActive: action === 'active' })).unwrap();
      }
      setIsStatusModalOpen(false);
      setStatusAction(null);
      dispatch(doGetClients({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (e) {
      // error handled globally
    }
  };

  const getStatusModalTitle = () => {
    if (!statusAction) return '';
    const { action } = statusAction;
    if (action === 'delete') return 'Confirm Delete User';
    if (action === 'inactive') return 'Confirm Deactivate User';
    return 'Confirm Activate User';
  };

  const getStatusModalContent = () => {
    if (!statusAction) return '';
    const { record, action } = statusAction;
    const name = record.name || 'this user';
    if (action === 'delete') {
      return `Are you sure you want to delete user "${name}"? This action will permanently clear their account data and cannot be undone.`;
    }
    if (action === 'inactive') {
      return `Are you sure you want to deactivate user "${name}"?`;
    }
    return `Are you sure you want to activate user "${name}"?`;
  };

  const withAction = (record) => {
    return (
      <div className="actions">
        <Button type="link" className="edit" onClick={() => navigate(`/clients/${record.id}`)}>
          <ReactSVG className="icon" src="/icons/edit.svg" />
        </Button>
        <div className="line"></div>
        <Button type="link" className="more">
          <ReactSVG className="icon" src="/icons/more.svg" />
        </Button>
      </div>
    );
  };

  return (
    <Spin spinning={false}>
      <div className="Content ClientsPage">
        <div className="ContentHeader">
          <div className="HeaderTitle">CLIENTS</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons">
              <Link to="/clients/new">
                <Button className="CreateButton ButtonPrimary" type="primary" size="small">
                  Create New Client
                  <ReactSVG className="icon" src="/icons/add.svg" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="broadcast-clients" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Broadcast push notification to all clients</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input.TextArea
              placeholder="Type a message to send to all clients"
              autoSize={{ minRows: 2, maxRows: 4 }}
              value={broadcastMessage}
              onChange={handleChangeBroadcastMessage}
              disabled={isFetching}
            />
            <Button
              type="primary"
              size="small"
              onClick={handleSendBroadcast}
              disabled={!broadcastMessage.trim() || isFetching}
            >
              Send push
            </Button>
          </div>
        </div>

        {/* Broadcast confirm modal */}
        <Modal
          title="Confirm Broadcast Notification"
          open={isConfirmModalOpen}
          onOk={handleConfirmBroadcast}
          onCancel={() => setIsConfirmModalOpen(false)}
          footer={null}
        >
          <div style={{ marginBottom: 16 }}>Are you sure you want to send this message to all selected clients?</div>
          <div
            style={{
              padding: '12px',
              background: '#f5f5f5',
              borderRadius: '4px',
              border: '1px solid #d9d9d9',
              whiteSpace: 'pre-wrap',
              marginBottom: 24,
            }}
          >
            {broadcastMessage}
          </div>
          <div className="FormFooter">
            <div className="Right">
              <Button
                onClick={() => {
                  setIsConfirmModalOpen(false);
                  setBroadcastMessage('');
                }}
                style={{ marginRight: 8 }}
              >
                Cancel
              </Button>
              <Button type="primary" loading={isFetching} onClick={handleConfirmBroadcast}>
                Accept
              </Button>
            </div>
          </div>
        </Modal>

        {/* Status change confirm modal */}
        <Modal
          title={getStatusModalTitle()}
          open={isStatusModalOpen}
          onCancel={() => {
            setIsStatusModalOpen(false);
            setStatusAction(null);
          }}
          footer={null}
        >
          <div style={{ marginBottom: 24 }}>{getStatusModalContent()}</div>
          <div className="FormFooter">
            <div className="Right">
              <Button
                onClick={() => {
                  setIsStatusModalOpen(false);
                  setStatusAction(null);
                }}
                style={{ marginRight: 8 }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                danger={statusAction?.action === 'delete'}
                loading={isFetching}
                onClick={handleConfirmStatusChange}
              >
                {statusAction?.action === 'delete' ? 'Delete' : 'Confirm'}
              </Button>
            </div>
          </div>
        </Modal>

        <div className="orders-header-table page-header-table">
          <div className="search">
            <Input
              placeholder="Search"
              suffix={<img src="/icons/search.svg" />}
              onChange={handleChangeSearch}
              onBlur={handleSearch}
              onKeyDown={handleKeyDown}
              disabled={isFetching}
              value={searchValue}
            />
          </div>
          <div className="buttons">
            {/* <Button className="FilterButton ButtonFilter">
              Action <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
            </Button>
            <Button className="FilterButton ButtonFilter" onClick={() => setIsFilterOpen(!isFilterOpen)}>
              Filter
              <ReactSVG className="icon" src="/icons/filter_list.svg" />
            </Button> */}
          </div>
        </div>
        {/* {isFilterOpen && <JobOrdersFilter isFetching={false} query={query} onSubmit={handleFilter} />} */}
        <div className="ContentMain">
          <UsersTable
            dataSource={clients}
            pagination={pagination}
            withAction={withAction}
            selectedRowKeys={selectedRowKeys}
            onSelectChange={onSelectChange}
            onStatusChange={handleStatusChange}
            rowKey="id"
            isFetching={false}
            onChange={handleTableChange}
          />
        </div>
      </div>
    </Spin>
  );
};

export default ClientsPage;
